import ClipperLib from "clipper-lib";
import { findMinBoundingRect } from "min-bounding-rectangle";

const earthCircumference = 40075000;
const metersInOneDegreeLat = 111139;

const oneDegreeLongInMeters = lat => earthCircumference * Math.cos(lat / 180 * Math.PI) / 360;

export class Vector3D {
    constructor(x, y, z) {
        this.x = x;
        this.y = y;
        this.z = z;
    }

    // Calculate the length of the vector
    length() {
        return Math.sqrt(this.x * this.x + this.y * this.y + this.z * this.z);
    }

    zRotationAngle() {
        return Math.atan2(this.y, this.x);
    }

    // Scale the vector by a scalar value
    scale(scalar) {
        return new Vector3D(this.x * scalar, this.y * scalar, this.z * scalar);
    }

    rotateAroundZ(angleRadians) {
        const cos = Math.cos(angleRadians);
        const sin = Math.sin(angleRadians);
        const xNew = this.x * cos - this.y * sin;
        const yNew = this.x * sin + this.y * cos;
        return new Vector3D(xNew, yNew, this.z);
    }

    add(other) {
        return Vector3D.add(this, other);
    }

    subtract(other) {
        return Vector3D.subtract(this, other);
    }

    dot(other) {
        return Vector3D.dot(this, other);
    }


    cross(other) {
        return Vector3D.cross(this, other);
    }

    normalize() {
        const len = this.length();
        return len === 0 ? new Vector3D(0,0,0) : this.scale(1 / len);
    }

    // Subtract two vectors (v1 - v2)
    static subtract(v1, v2) {
        return new Vector3D(v1.x - v2.x, v1.y - v2.y, v1.z - v2.z);
    }

    // Add two vectors
    static add(v1, v2) {
        return new Vector3D(v1.x + v2.x, v1.y + v2.y, v1.z + v2.z);
    }

    // Dot product of two vectors
    static dot(v1, v2) {
        return v1.x * v2.x + v1.y * v2.y + v1.z * v2.z;
    }

    // Cross product of two vectors
    static cross(v1, v2) {
        const x = v1.y * v2.z - v1.z * v2.y;
        const y = v1.z * v2.x - v1.x * v2.z;
        const z = v1.x * v2.y - v1.y * v2.x;
        return new Vector3D(x, y, z);
    }

    // angle between vectors
    static angle2D(v1, v2) {
        return Math.atan2(v1.x*v2.y - v1.y*v2.x, this.dot(v1, v2));
    }

    static angle3D(v1, v2) {
        const dotProduct = Vector3D.dot(v1,v2);
        const lengthV1 = v1.length();
        const lengthV2 = v2.length();

        const cosAngle = dotProduct / (lengthV1 * lengthV2);

        // Clamp value to [-1, 1] to avoid NaN due to floating-point errors
        const clamped = Math.max(-1, Math.min(1, cosAngle));
        const angleRadians = Math.acos(clamped);

        return angleRadians;
    }
}

const boundingBoxToArea = bb => {
    // xmin, ymin
    const [bottomLeft, topLeft, , bottomRight] = bb;
    const origin = bottomLeft;
    const bottomEdge = Vector3D.subtract(bottomRight, origin);
    const leftEdge = Vector3D.subtract(topLeft, origin);
    //const height = Math.max(...[bottomLeft, topLeft, topRight, bottomRight].map(e => e.z));

    return {
        originX: origin.x,
        originY: origin.y,
        //originZ: height,
        originZ: origin.z,
        uaxisX: bottomEdge.x,
        uaxisY: bottomEdge.y,
        uaxisZ: 0,
        //uaxisZ: height,
        //uaxisZ: bottomEdge.z,
        vaxisX: leftEdge.x,
        vaxisY: leftEdge.y,
        //vaxisZ: height,
        vaxisZ: leftEdge.z,
        calculation: true,
        dumping: false,
        overview3dVisible: true,
    };
};

// const crossProduct = (a, b, c) => (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x);

// const getConvexHull = providedPoints => {
//     const points = [...providedPoints];
//     points.sort((a, b) => (a.x !== b.x ? a.x - b.x : a.y - b.y));

//     const upper = [];
//     for (let p of points) {
//         while (upper.length >= 2 && crossProduct(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) {
//             upper.pop();
//         }
//         upper.push(p);
//     }

//     const lower = [];
//     for (let i = points.length - 1; i >= 0; i--) {
//         let p = points[i];
//         while (lower.length >= 2 && crossProduct(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) {
//             lower.pop();
//         }
//         lower.push(p);
//     }

//     upper.pop();
//     lower.pop();
//     return [...upper, ...lower, upper[0]];
// };

const getBoundaryPolygons = surfacePolygons => {
    const scale = 100;
    const subjectPaths = surfacePolygons.map(polygon => polygon.points.map(p => ({X: p.x, Y: p.y})));
    ClipperLib.JS.ScaleUpPaths(subjectPaths, scale);
    const cpr = new ClipperLib.Clipper();
    cpr.AddPaths(subjectPaths, ClipperLib.PolyType.ptSubject, true);
    const subjectFillType = ClipperLib.PolyFillType.pftNonZero;
    const clipType = ClipperLib.ClipType.ctUnion;
    const solutionPaths = new ClipperLib.Paths();
    cpr.Execute(clipType, solutionPaths, subjectFillType, []);
    return solutionPaths.map(path => path.map(p => ({x: p.X / scale, y: p.Y / scale})));
};

class Lod2 {
    convertBuildingPointsToHorizontalArea(building) {
        const buildingPoints = building.map(b => b.points).flat();
        const highestZ = buildingPoints.reduce((acc, val) => Math.max(acc, val.z), Number.NEGATIVE_INFINITY);
        const pointsList = buildingPoints.map(p => ({...p, z: highestZ}));
        const clacInput = pointsList.map(p => [p.x, p.y]);
        const boundingBox = findMinBoundingRect(clacInput).map(e => new Vector3D(e[0], e[1], highestZ));
        const area = boundingBoxToArea(boundingBox);
        const boundary = getBoundaryPolygons(building);
        if(boundary) {
            return {...area, boundary, boundaryClipped: true};
        }
        return {...area, boundaryClipped: false};
    }

    // https://stackoverflow.com/questions/639695/how-to-convert-latitude-or-longitude-to-meters
    metersOffsetToLongLat(offset, originLat) {
        const diffLat = offset / metersInOneDegreeLat;
        const diffLong = offset / oneDegreeLongInMeters(originLat);
        return { diffLong, diffLat };
    }

    logLatToXYMeters(long, lat) {
        // https://stackoverflow.com/questions/639695/how-to-convert-latitude-or-longitude-to-meters
        const y = lat * metersInOneDegreeLat;
        const x = oneDegreeLongInMeters(lat) * long;
        return { x, y };
    }
}
export default new Lod2();